import { getToken, TokenInfo } from "../config/tokens";
import { CollateralAssetResponse } from "../codegen-api";
import {
  AllCollaterals,
  DepositWithdrawCollaterals,
} from "../constants/addresses";

export const getDepositTokenSymbol = (asset: string) => {
  const normalized = asset.trim();
  const upper = normalized.toUpperCase();

  if (upper === "ETH" || upper === "METH" || upper === "WETH") {
    return "WETH";
  }

  if (
    upper === "BTC" ||
    upper === "MBTC" ||
    upper === "MWBTC" ||
    upper === "WBTC"
  ) {
    return "WBTC";
  }

  if (upper === "MUSDC") {
    return "USDC";
  }

  return normalized;
};

export const getDepositTokenInfo = (
  chainId: string | number,
  asset: string
): TokenInfo | undefined => getToken(chainId, getDepositTokenSymbol(asset));

export const getDepositFlowAsset = (
  collateral: AllCollaterals | string
): DepositWithdrawCollaterals | undefined => {
  const value = String(collateral).trim();
  if (
    Object.values(DepositWithdrawCollaterals).includes(
      value as DepositWithdrawCollaterals
    )
  ) {
    return value as DepositWithdrawCollaterals;
  }

  switch (getDepositTokenSymbol(value).toUpperCase()) {
    case CollateralAssetResponse.Usdc:
      return DepositWithdrawCollaterals.Usdc;
    case CollateralAssetResponse.Weth:
      return DepositWithdrawCollaterals.Weth;
    case CollateralAssetResponse.Wbtc:
      return DepositWithdrawCollaterals.Wbtc;
    default:
      return undefined;
  }
};

export const getTransferFlowAsset = (
  collateral: AllCollaterals | string
): DepositWithdrawCollaterals | undefined => {
  const value = String(collateral).trim();
  const upper = value.toUpperCase();

  if (upper === "MON" || upper === "WMON" || upper === "WNATIVE") {
    return DepositWithdrawCollaterals.Weth;
  }

  return getDepositFlowAsset(value);
};

// The row's name, whichever field carries it: live payloads send `asset_id`
// ("USDC"), some send `asset_name`, older ones the bare string. Reading only
// `asset_name` made every name comparison fail for a row that has an id, so a
// match was only ever found when the caller also knew the token address.
const getCollateralName = (collateralAsset: any) =>
  typeof collateralAsset === "string"
    ? collateralAsset
    : collateralAsset?.asset_id || collateralAsset?.asset_name || "";

const getCollateralAddress = (collateralAsset: any) =>
  typeof collateralAsset === "string"
    ? ""
    : collateralAsset?.asset_address || "";

const normalizeAddress = (address?: string) => address?.toLowerCase() || "";

export const findMatchingSpotCollateral = (
  collaterals: any[] | undefined,
  asset: string,
  tokenAddress?: string
) => {
  const targetSymbol = getDepositTokenSymbol(asset);
  const targetAddress = normalizeAddress(tokenAddress);

  return collaterals?.find((collateral) => {
    const collateralAsset = collateral.collateral_asset;
    const rawAddress = normalizeAddress(getCollateralAddress(collateralAsset));
    if (targetAddress && rawAddress) {
      return rawAddress === targetAddress;
    }

    const rawName = getCollateralName(collateralAsset);
    return (
      rawName === asset ||
      rawName === targetSymbol ||
      getDepositTokenSymbol(rawName) === targetSymbol
    );
  });
};
